import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { useRememberViewedRow, useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { getZmHandOutStatCards } from '@/utils/handOutStatCards';
import { promoMembersQueryOptions } from '../api/promotion.api';
import { usePromoAdjustmentNavigation } from '../hooks/usePromoAdjustmentNavigation';
import type { PromoAdjustmentUnit, PromoDrillParams } from '../types/promotion.types';
import { getPromoMemberColumns } from './PromoColumns';

interface PromoItemZmsViewProps {
  unit: PromoAdjustmentUnit;
  promoItemId: number;
  zmSearch: PromoDrillParams;
  title: string | undefined;
}

export function PromoItemZmsView({ unit, promoItemId, zmSearch, title }: PromoItemZmsViewProps) {
  const { openAdjustmentList } = usePromoAdjustmentNavigation(unit);
  useRememberViewedRow('promoItems', promoItemId);
  const lastViewedZmId = useRevealLastViewedRow('promoItemZms', promoItemId);

  const zmsQuery = useQuery(promoMembersQueryOptions(promoItemId, zmSearch));

  const columns = useMemo(
    () => getPromoMemberColumns(unit, promoItemId, zmSearch),
    [unit, promoItemId, zmSearch],
  );

  return (
    <DetailTableLayout
      title={title}
      titleAddon={<DateRangeBadge dateFrom={zmSearch.dateFrom} dateTo={zmSearch.dateTo} />}
      onNoHistory={() => openAdjustmentList(zmSearch)}
      stats={getZmHandOutStatCards(zmsQuery.data)}
      table={{
        rows: zmsQuery.data ?? [],
        columns,
        isLoading: zmsQuery.isLoading,
        error: zmsQuery.error,
        errorTitle: 'Could not load the ZMs',
        onRetry: () => void zmsQuery.refetch(),
        stickyColumnId: 'name',
        emptyMessage: 'Nobody handed this item out in the period',
        isRowHighlighted: (zm) => zm.userId === lastViewedZmId,
      }}
    />
  );
}
